import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import AdminLayout from '../../components/admin/layout/AdminLayout';
import { apiClient } from '../../services/api';
import {
  FiArrowLeft,
  FiEdit3,
  FiCheckCircle,
  FiXCircle,
  FiMapPin,
  FiPhone,
  FiMail,
  FiGlobe,
  FiCalendar,
  FiUsers,
  FiStar,
  FiEye,
  FiMessageSquare,
  FiShield,
  FiFileText,
  FiCreditCard,
  FiHome
} from 'react-icons/fi';
import { FaLine, FaFacebook } from 'react-icons/fa';

interface CompanyImage {
  id: string;
  imageUrl: string;
  cloudinaryPublicId: string;
  caption?: string;
  altText?: string;
  displayOrder: number;
}

interface Company {
  id: string;
  slug: string;
  nameTh: string;
  nameEn?: string;
  description?: string;
  logoUrl?: string;
  phone1: string;
  phone2?: string;
  phone3?: string;
  email: string;
  website?: string;
  lineId?: string;
  facebookUrl?: string;
  address: string;
  province: string;
  district: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  serviceTypes: string[];
  serviceAreas: string[];
  companySize?: string;
  establishedYear?: number;
  licenseNumber?: string;
  registrationId?: string;
  taxId?: string;
  isActive: boolean;
  isFeatured: boolean;
  isVerified: boolean;
  verifiedAt?: string;
  viewCount: number;
  contactCount: number;
  galleryImages?: CompanyImage[]; // ✅ ADD: Gallery images from API response
  createdAt: string;
  updatedAt: string;
}

const CompanyDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchCompanyDetails(id);
    }
  }, [id]);

  const fetchCompanyDetails = async (companyId: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get<Company>(`/api/admin/companies/${companyId}`);
      setCompany(response.data);

      // ✅ FIX: Gallery images are already included in the API response
      // No need to fetch separately - data.galleryImages contains all images
    } catch (err: any) {
      console.error('Error fetching company details:', err);
      setError(err.message || 'เกิดข้อผิดพลาดในการดึงข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/companies');
  };

  const handleEdit = () => {
    if (company) {
      navigate(`/admin/companies/edit/${company.id}`);
    }
  };

  const handleStatusChange = async (newStatus: 'active' | 'featured' | 'verified', value: boolean) => {
    if (!company) return;

    try {
      let endpoint = '';

      if (newStatus === 'verified') {
        endpoint = value ? 'verify' : 'unverify';
      } else if (newStatus === 'featured') {
        endpoint = 'feature';
      }

      if (endpoint) {
        await apiClient.post(`/api/admin/companies/${company.id}/${endpoint}`);

        // Refresh company data
        await fetchCompanyDetails(company.id);
        toast.success('อัปเดตสถานะเรียบร้อย');
      }
    } catch (err: any) {
      console.error('Error updating status:', err);
      toast.error(err.message || 'เกิดข้อผิดพลาดในการอัปเดตสถานะ');
    }
  };

  const breadcrumbs = [
    { label: 'จัดการบริษัท', icon: FiHome, path: '/admin/companies' },
    { label: company?.nameTh || 'รายละเอียดบริษัท', icon: FiFileText }
  ];

  if (loading) {
    return (
      <AdminLayout title="กำลังโหลด..." breadcrumbs={breadcrumbs}>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !company) {
    return (
      <AdminLayout title="ข้อผิดพลาด" breadcrumbs={breadcrumbs}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <FiXCircle className="h-6 w-6 text-red-600 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-red-900">เกิดข้อผิดพลาด</h3>
              <p className="text-red-700 mt-1">{error || 'ไม่พบข้อมูลบริษัท'}</p>
            </div>
          </div>
          <button
            onClick={handleBack}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <FiArrowLeft className="mr-2 h-4 w-4" />
            กลับไปหน้ารายการ
          </button>
        </div>
      </AdminLayout>
    );
  }

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
        <FiCheckCircle className="w-4 h-4 mr-1" />
        ใช้งานอยู่
      </span>
    ) : (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
        <FiXCircle className="w-4 h-4 mr-1" />
        ไม่ใช้งาน
      </span>
    );
  };

  const getSizeLabel = (size?: string) => {
    const sizeMap: Record<string, string> = {
      'SMALL': 'เล็ก (1-50 คน)',
      'MEDIUM': 'กลาง (51-200 คน)',
      'LARGE': 'ใหญ่ (201-500 คน)',
      'ENTERPRISE': 'องค์กรขนาดใหญ่ (500+ คน)'
    };
    return sizeMap[size || ''] || 'ไม่ระบุ';
  };

  return (
    <AdminLayout
      title={company.nameTh}
      breadcrumbs={breadcrumbs}
    >
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <FiArrowLeft className="mr-2 h-4 w-4" />
            กลับ
          </button>
          <button
            onClick={handleEdit}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <FiEdit3 className="mr-2 h-4 w-4" />
            แก้ไขข้อมูล
          </button>
        </div>

        {/* Company Header Card */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {company.logoUrl ? (
                  <img
                    src={company.logoUrl}
                    alt={company.nameTh}
                    className="h-20 w-20 rounded-lg object-cover"
                  />
                ) : (
                  <div className="h-20 w-20 rounded-lg bg-gray-300 flex items-center justify-center">
                    <FiUsers className="h-10 w-10 text-gray-600" />
                  </div>
                )}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{company.nameTh}</h2>
                  {company.nameEn && (
                    <p className="text-lg text-gray-600 mt-1">{company.nameEn}</p>
                  )}
                  <div className="flex items-center space-x-3 mt-2">
                    {getStatusBadge(company.isActive)}
                    {company.isVerified && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        <FiShield className="w-4 h-4 mr-1" />
                        ยืนยันแล้ว
                      </span>
                    )}
                    {company.isFeatured && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                        <FiStar className="w-4 h-4 mr-1" />
                        แนะนำ
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center space-x-6 text-sm text-gray-500">
                  <div className="flex items-center">
                    <FiEye className="h-5 w-5 mr-1" />
                    <span>{company.viewCount.toLocaleString()} ครั้ง</span>
                  </div>
                  <div className="flex items-center">
                    <FiMessageSquare className="h-5 w-5 mr-1" />
                    <span>{company.contactCount.toLocaleString()} ครั้ง</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Status Actions */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={company.isVerified}
                  onChange={(e) => handleStatusChange('verified', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  ยืนยันบริษัท
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={company.isFeatured}
                  onChange={(e) => handleStatusChange('featured', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  แนะนำบริษัท
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Company Description */}
            {company.description && (
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">คำอธิบายบริษัท</h3>
                </div>
                <div className="px-6 py-5">
                  <p className="text-gray-700 whitespace-pre-line">{company.description}</p>
                </div>
              </div>
            )}

            {/* Service Types */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-6 py-5 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">ประเภทบริการ</h3>
              </div>
              <div className="px-6 py-5">
                {company.serviceTypes.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {company.serviceTypes.map((service, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">ไม่มีข้อมูล</p>
                )}
              </div>
            </div>

            {/* Service Areas */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-6 py-5 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">พื้นที่ให้บริการ</h3>
              </div>
              <div className="px-6 py-5">
                {company.serviceAreas.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {company.serviceAreas.map((area, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                      >
                        <FiMapPin className="w-3 h-3 mr-1" />
                        {area}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">ไม่มีข้อมูล</p>
                )}
              </div>
            </div>

            {/* Gallery Section */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-6 py-5 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  แกลเลอรีรูปภาพ
                  {company.galleryImages && company.galleryImages.length > 0 && (
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      ({company.galleryImages.length} รูป)
                    </span>
                  )}
                </h3>
              </div>
              <div className="px-6 py-5">
                {company.galleryImages && company.galleryImages.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {company.galleryImages
                      .sort((a, b) => a.displayOrder - b.displayOrder)
                      .map((image, index) => (
                        <div
                          key={image.id}
                          className="relative group rounded-lg overflow-hidden border border-gray-200 hover:border-blue-400 transition-colors"
                        >
                          <div className="aspect-w-16 aspect-h-9 bg-gray-100">
                            <img
                              src={image.imageUrl}
                              alt={image.altText || image.caption || `${company.nameTh} - รูปภาพที่ ${index + 1}`}
                              className="object-cover w-full h-full"
                              loading="lazy"
                            />
                          </div>
                          {image.caption && (
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                              <p className="text-white text-sm font-medium truncate">
                                {image.caption}
                              </p>
                            </div>
                          )}
                          {/* Display Order Badge */}
                          <div className="absolute top-2 left-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-600 text-white shadow-lg">
                              #{image.displayOrder + 1}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                      <FiFileText className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 text-sm">ยังไม่มีรูปภาพในแกลเลอรี</p>
                    <p className="text-gray-400 text-xs mt-1">สามารถอัปโหลดรูปภาพได้สูงสุด 6 รูป</p>
                  </div>
                )}
              </div>
            </div>

            {/* License & Registration Info (Private Admin Data) */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg border-l-4 border-yellow-400">
              <div className="px-6 py-5 border-b border-gray-200 bg-yellow-50">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <FiShield className="h-5 w-5 mr-2 text-yellow-600" />
                  ข้อมูลส่วนตัว (Admin เท่านั้น)
                </h3>
              </div>
              <div className="px-6 py-5">
                <dl className="grid grid-cols-1 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">เลขใบอนุญาต</dt>
                    <dd className="mt-1 text-sm text-gray-900">{company.licenseNumber || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">เลขทะเบียนนิติบุคคล</dt>
                    <dd className="mt-1 text-sm text-gray-900">{company.registrationId || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">เลขประจำตัวผู้เสียภาษี</dt>
                    <dd className="mt-1 text-sm text-gray-900">{company.taxId || '-'}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>

          {/* Right Column - Contact & Details */}
          <div className="space-y-6">
            {/* Contact Information */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-6 py-5 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">ข้อมูลติดต่อ</h3>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div className="flex items-start">
                  <FiPhone className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">เบอร์โทรศัพท์</p>
                    <p className="text-sm text-gray-900">{company.phone1}</p>
                    {company.phone2 && <p className="text-sm text-gray-900">{company.phone2}</p>}
                    {company.phone3 && <p className="text-sm text-gray-900">{company.phone3}</p>}
                  </div>
                </div>

                <div className="flex items-start">
                  <FiMail className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">อีเมล</p>
                    <p className="text-sm text-gray-900">{company.email}</p>
                  </div>
                </div>

                {company.website && (
                  <div className="flex items-start">
                    <FiGlobe className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700">เว็บไซต์</p>
                      <a
                        href={company.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline"
                      >
                        {company.website}
                      </a>
                    </div>
                  </div>
                )}

                {company.lineId && (
                  <div className="flex items-start">
                    <FaLine className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700">LINE ID</p>
                      <p className="text-sm text-gray-900">@{company.lineId}</p>
                    </div>
                  </div>
                )}

                {company.facebookUrl && (
                  <div className="flex items-start">
                    <FaFacebook className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700">Facebook</p>
                      <a
                        href={company.facebookUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Facebook Page
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Location Information */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-6 py-5 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">ที่อยู่</h3>
              </div>
              <div className="px-6 py-5 space-y-3">
                <div className="flex items-start">
                  <FiMapPin className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{company.address}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {company.district}, {company.province} {company.postalCode}
                    </p>
                  </div>
                </div>

                {company.latitude && company.longitude && (
                  <div className="pt-3 border-t border-gray-200">
                    <a
                      href={`https://www.google.com/maps?q=${company.latitude},${company.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline inline-flex items-center"
                    >
                      <FiMapPin className="h-4 w-4 mr-1" />
                      ดูบน Google Maps
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Company Details */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-6 py-5 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">รายละเอียดบริษัท</h3>
              </div>
              <div className="px-6 py-5 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">ขนาดบริษัท</span>
                  <span className="text-sm font-medium text-gray-900">
                    {getSizeLabel(company.companySize)}
                  </span>
                </div>

                {company.establishedYear && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">ปีที่ก่อตั้ง</span>
                    <span className="text-sm font-medium text-gray-900">
                      {company.establishedYear}
                    </span>
                  </div>
                )}

                <div className="flex justify-between pt-3 border-t border-gray-200">
                  <span className="text-sm text-gray-500">สร้างเมื่อ</span>
                  <span className="text-sm text-gray-900">
                    {new Date(company.createdAt).toLocaleDateString('th-TH', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">อัปเดตล่าสุด</span>
                  <span className="text-sm text-gray-900">
                    {new Date(company.updatedAt).toLocaleDateString('th-TH', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>

                {company.verifiedAt && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">ยืนยันเมื่อ</span>
                    <span className="text-sm text-gray-900">
                      {new Date(company.verifiedAt).toLocaleDateString('th-TH', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Statistics */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-6 py-5 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">สถิติ</h3>
              </div>
              <div className="px-6 py-5 space-y-3">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <FiEye className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-500">ยอดดู</span>
                  </div>
                  <span className="text-lg font-semibold text-blue-600">
                    {company.viewCount.toLocaleString()}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <FiMessageSquare className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-500">ยอดติดต่อ</span>
                  </div>
                  <span className="text-lg font-semibold text-green-600">
                    {company.contactCount.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CompanyDetailView;
